import Box from "../Box";
import AlertRequestQuery from "~/entities/AlertRequestQuery";
import ValidationModule from "~/entities/ValidationModule";

export default class ValidationCosts extends ValidationModule {
  public pickupAlerts = {
    selector: {
      warning: "PickupWarning",
      pickup: "AlertSameDayPickup",
      existing: "ExistingPickup",
    },
    needle: ["#PICKUPDATE#", "#DELIVERYDATEWITHPICKUP#"],
  };

  public costAlerts = {
    fees: "NoDutiesAndTaxes",
  };

  public contentAlerts = {
    selector: {
      USeBike: "AlertEbikeShipment",
      eBike: "AlertEBikeInternational",
    },
    needle: [""],
  };

  public loginModalCompleted = false;

  public constructor() {
    super("Costs");
    this.alertNames = [
      "AlertInvalidFromCountry", // We do not currently offer shipping services from #CountryName#
      "AlertInvalidCountry", // We do not currently offer shipping services to #CountryName#.
      "MultiplePackageHighValue", // Some of the packages in your shipment qualify for Preferred Handling. To ensure the whole shipment receives the same service level, we suggest you select Protection levels for each that include Preferred Handling.
      "AlertPostalCodeLookup", // Unable to lookup your postal code right now, no problem though.
      "AlertEbikeShipment", // BikeFlights’ e-bike shipping program is for safely shipping single e-bikes that have their battery either contained within the frame or fully installed.
      "AlertEBikeInternational", // E-bike shipping is currently available via ground service within the contiguous U.S.
      "AlertFedExFailed", // We were not able to get rates from the information you provided. Typically the reason is that you gave us too much or too little detail. Try typing something as simple as a basic street address or a city, state, and zip code combination to get rates. Thank you.
      "AlertFedExCanadaNoResults", // Sorry! We were not able to get rates from the information you provided. Typically the reason is that you gave us too much or too little detail. Try typing something as simple as a basic street address or a city and state combination to get rates. Thank you.

      "PickupWarning", // The first available date for pickup is #PICKUPDATE#, moving your delivery to #DELIVERYDATEWITHPICKUP#.
      "AlertSameDayPickup", // You can drop off today at one of our partner carrier locations. If you would like a pickup, the first available date is #PICKUPDATE#.
      "AlertCAPersonalEffects", // U.S. and Canadian Customs only permit sales, repairs and gifts to ship Ground. You must select Int Economy or Int Priority to ship your bike as personal effects when traveling.
      "KRPostalCodeConversion", // We've converted the older 6 digit Korean Postal code to the new 5 digit format to return rates. If not correct, you can correct at Shipping details.
      "LocationChangedRequote", // You changed a location on shipping details, please select a speed for the new location and we will take you right back to where you were.
      "NoDutiesAndTaxes", // We did not receive the Estimated Clearance Costs for your shipment. After you complete your order, a Support Team Member will contact you to provide an estimate, collect payment and any additional required documentation prior to shipping.
      "PersonalGoods1", // The prices below include carrier costs as well as international clearance costs. After completing this order, a BikeFlights Support Team member will contact you to collect additional required documentation.
      "EUtoEUCommercial", // The prices below include carrier costs as well as international clearance costs for your shipment. After completing this order, a BikeFlights Support Team member will contact you to collect additional required documentation.
      "HighValueDirectToUPS", // Due to the value and/or purpose of your shipment, you must give your package(s) directly to a UPS Driver or UPS Customer Center representative for them to receive preferred handling. Your packages will NOT get preferred handling if you drop them off at UPS Stores, other third party retailers (including Authorized Shipping Outlets) or UPS Drop Boxes.
      "ExistingPickup", // Please confirm permission, location and timing of an existing pickup with the bike shop, hotel or business. When in doubt, it is always best to schedule a one-time pickup to be sure your shipment gets moving.
      "AlertEbikeShipment", // BikeFlights’ e-bike shipping program is for safely shipping single e-bikes that have their battery either contained within the frame or fully installed.
      "AlertEBikeInternational", // E-bike shipping is currently available via ground service within the contiguous U.S.
    ];
    this.alertTypes = {
      pickup: this.pickupAlerts,
      cost: this.costAlerts,
      content: this.contentAlerts,
    };
  }

  public async Validate(scope: string = "") {
    // for later Validate needs some kind of flag to asks for a particular validation type, depth or scope. so that only that method is called
    this.validated = true;
    let errors = this.validatePickupSelection();
    for (let step = 0; step < this.component?.trips.length; step++) {
      if (!this.component?.pickupDropOffSelected[step]) {
        errors++;
        this.validationErrors.push("noPickup-" + step);
      }
      if (!this.component?.shipDates[step]) {
        errors++;
        this.validationErrors.push("noDate-" + step);
      }
    }
    if (!(this.component && this.component.selectedRateSpeed)) {
      errors++;
    }
    if (errors === 0) {
      this.validated = true;
      this.validationMade = true;
    } else {
      this.validated = false;
      this.validationMade = true;
    }
    // get session and ask if the user was set
    const session = this.component.session;
    if (session && session.user && session.user.email) {
      this.loginModalCompleted = true;
    }
    return !errors;
  }

  public resetValidations() {
    this.validated = false;
    this.validationMade = false;
    this.validationErrors = [];
    this.component?.clearActiveNotices();
  }

  public requestAlert(data: any) {
    this.component?.requestAlert(this.getFilteredAlertKey(data, "asObject"));
  }

  public addNotice(data: any) {
    this.component?.addNotice(this.getFilteredAlertKey(data));
  }

  public removeNotice(data: any) {
    this.component?.removeNotice(this.getFilteredAlertKey(data));
  }

  protected getFilteredAlertKey(
    data: any,
    returnType: string = "asString"
  ): any {
    let keyGetter = new AlertRequestQuery();
    if (data.hasOwnProperty("type")) {
      switch (data.type) {
        case "pickup":
          keyGetter.name =
            this.alertTypes[data.type].selector[
              data.event.hasPickup
                ? "pickup"
                : data.event.hasPickup === false
                ? "existing"
                : "warning"
            ];
          keyGetter.typeSelector = "pickup";
          keyGetter.replacements = {
            [this.alertTypes[data.type].needle[0]]:
              data.event.nextAvailableDate,
            [this.alertTypes[data.type].needle[1]]:
              data.event.pickupDeliveryDate,
          };
          break;
        case "cost":
          keyGetter.name = this.alertTypes[data.type][data.event];
          keyGetter.typeSelector = data.type;
          break;
        case "content":
          keyGetter.name = this.alertTypes[data.type].selector[data.name];
          keyGetter.typeSelector = data.name;
          break;
      }
    } else {
      // fill
    }
    const filteredData = keyGetter.getKey(
      this.alertNames,
      this.alertTypes[data.type]
    );
    if (returnType === "asString") {
      return filteredData.name;
    } else {
      return filteredData;
    }
  }

  validatePickupSelection(scope = ""): number {
    if (!(scope && scope === "requoted")) {
      this.resetValidations();
    }
    this.validatePackageSelection();
    let errors = 0;
    const component = this.component;
    if (component) {
      for (let step = 0; step < this.component?.trips.length; step++) {
        if (
          component.pickupDropOffSelected[step] &&
          component.shipDates[step]
        ) {
          const pickupOption = component.pickupOption;
          // Check if user has pickup selected (hasPickup === 1)
          const hasPickupSelected = pickupOption?.[step]?.hasPickup === 1;
          const dateSelection = component.shipDates[step];
          const before = component.isBeforeOrToday(
            new Date(dateSelection),
            true
          );
          
          // Only show warning if user has pickup selected AND date is today or before
          if (hasPickupSelected && before) {
            const nextAvailableDate =
              component.getNextAvailableDate(dateSelection);
            this.requestAlert({
              event: {
                hasPickup: true,
                pickupDeliveryDate: dateSelection,
                nextAvailableDate,
              },
              type: "pickup",
            });
            this.validationErrors.push("noPickup-" + step);
            errors++;
          } else if (
            pickupOption &&
            pickupOption[step] &&
            pickupOption[step]?.hasPickup === false
          ) {
            this.requestAlert({ event: { hasPickup: false }, type: "pickup" });
          }
          if (component.selectedRateSpeed) {
            if (
              component.selectedRateSpeed.ship &&
              component.isBeforeOrToday(
                new Date(component.selectedRateSpeed.ship),
                false
              )
            ) {
              errors++;
            }
            if (
              component.selectedRateSpeed.upsDeliver &&
              component.isBeforeOrToday(
                new Date(component.selectedRateSpeed.upsDeliver),
                false
              )
            ) {
              errors++;
            }
            if (
              component.selectedRateSpeed.deliver &&
              component.isBeforeOrToday(
                new Date(component.selectedRateSpeed.deliver),
                false
              )
            ) {
              errors++;
            }
          }
        } else {
          errors++;
        }
      }
    }
    return errors;
  }

  public validatePackageSelection() {
    const boxes = this.component?.boxes;

    boxes?.forEach((box: Box, index: number) => {
      this.component?.cleanBoxErrors(index);
      // This box Size Values
      const thisContent = box.contents;
      // Validate E-bike notice
      if (!thisContent || !thisContent.label) {
        if (this.component && typeof this.component.setBoxError === "function")
          this.component.setBoxError({
            index: index,
            error: "categorySelectionInvalid",
          });
        this.validationErrors.push("categorySelectionInvalid");
      } else if (
        thisContent &&
        thisContent.label &&
        thisContent.label.includes("E-Bike")
      ) {
        const countries = this.component?.currentCountries;
        let nonUS = false;
        for (const property in countries) {
          if (countries[property] !== "US") {
            nonUS = true;
          }
        }
        if (nonUS) {
          this.requestAlert({ name: "eBike", type: "content" });
        } else {
          this.requestAlert({ name: "USeBike", type: "content" });
        }
      }
    });
  }

  validateCosts(scope = "") {
    if (!(scope && scope === "requoted")) {
      // added this because it was removing the requote alert after a few seconds after landing on costs. this is not ideal but will do for now. We need to add a better filtration to the reset validation super method
      this.resetValidations();
    }
    this.validatePackageSelection();
    this.validatePickupSelection(scope);
    let errors = 0;
    const component = this.component;
    if (component) {
      for (let step = 0; step < this.component?.fees.length; step++) {
        if (this.component.fees[step] && this.component.fees[step].landedCost) {
          for (
            let i = 0;
            i < this.component.fees[step].landedCost.length;
            i++
          ) {
            if (
              !this.component.fees[step].landedCost[i] ||
              !this.component.fees[step].landedCost[i].success
            ) {
              errors++;
            }
          }
        }
      }
    }
    if (errors) {
      this.requestAlert({ event: "fees", type: "cost" });
    }
  }

  public validateGoogleSelection() {
    if (this.component?.details) {
      for (let step = 0; step < this.component?.details.length; step++) {
        const to = this.component.details[step].to;
        const from = this.component.details[step].from;
        if (
          !to.passedAVS &&
          to.avsChecked &&
          to.searchType?.toLowerCase() === "full"
        ) {
          this.component?.addNotice("warningAVSFailed");
        }
        if (
          !from.passedAVS &&
          from.avsChecked &&
          from.searchType?.toLowerCase() === "full"
        ) {
          this.component?.addNotice("warningAVSFailed");
        }
      }
    }
  }
}
